import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Pencil, Plus, Printer, Search, Trash2 } from "lucide-react";
import { DsBadge, DsButton, DsIcon, DsInput } from "../../../../../design-system";
import InfoHint from "../holiday/InfoHint";
import {
  collectHolidayYears,
  formatHolidayTypeLabel,
  groupHolidaysByYear,
  holidayTypeTone,
  holidayYear,
} from "../../../../../lib/holidayBankUtils";

const HolidayStructureTab = ({
  darkMode,
  holidayPolicies,
  holidayBank,
  fetchAllMasterData,
  setShowHolidayModal,
  onCreateHolidayPolicy,
  setShowHolidayImportModal,
  setShowHolidayBankModal,
  handleEditHolidayPolicy,
  handleDeleteHolidayPolicy,
  handleEditHolidayBank,
  handleRemoveFromHolidayBank,
  formatDate,
  canCreate = true,
  canEdit = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openActionFor, setOpenActionFor] = useState(null);
  const [showHolidayBankPanel, setShowHolidayBankPanel] = useState(false);
  const [holidayBankSearch, setHolidayBankSearch] = useState("");
  const [bankYearFilter, setBankYearFilter] = useState("all");
  const [openBankActionFor, setOpenBankActionFor] = useState(null);

  const filteredPolicies = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return holidayPolicies;
    return holidayPolicies.filter((policy) =>
      String(policy?.name || "").toLowerCase().includes(q)
    );
  }, [holidayPolicies, searchTerm]);

  const bankYears = useMemo(
    () => collectHolidayYears(holidayBank),
    [holidayBank]
  );

  const filteredHolidayBank = useMemo(() => {
    const q = holidayBankSearch.trim().toLowerCase();
    return (holidayBank || []).filter((holiday) => {
      const y = holidayYear(holiday?.date);
      if (bankYearFilter !== "all" && y !== Number(bankYearFilter)) return false;
      if (!q) return true;
      const name = String(holiday?.name || "").toLowerCase();
      const date = String(holiday?.date || "").toLowerCase();
      return name.includes(q) || date.includes(q);
    });
  }, [holidayBank, holidayBankSearch, bankYearFilter]);

  const bankGrouped = useMemo(
    () => groupHolidaysByYear(filteredHolidayBank),
    [filteredHolidayBank]
  );
  const bankGroupedEntries = useMemo(
    () =>
      Array.from(bankGrouped.entries()).sort((a, b) => {
        const ay = typeof a[0] === "number" ? a[0] : 0;
        const by = typeof b[0] === "number" ? b[0] : 0;
        return by - ay;
      }),
    [bankGrouped]
  );

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
        <div className="ds-page-toolbar !rounded-none !border-x-0 !border-t-0 flex flex-wrap items-center justify-between gap-2 !shadow-none">
          <div className="flex items-center gap-2">
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowHolidayBankPanel(true)}
            >
              Holiday Bank
            </DsButton>
            <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Holiday Policies</h3>
            <InfoHint title="A holiday policy is a named calendar of bank holidays plus assignment and RH yearly cap." />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <DsIcon
                icon={Search}
                size="sm"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
              />
              <DsInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search policy..."
                darkMode={darkMode}
                className="w-56 pl-8"
              />
            </div>
            <DsButton type="button" variant="outline" size="sm">
              <DsIcon icon={Printer} size="sm" />
              Print All
            </DsButton>
            {canCreate !== false ? (
            <DsButton
              type="button"
              variant="primary"
              size="sm"
              onClick={async () => {
                if (onCreateHolidayPolicy) {
                  await onCreateHolidayPolicy();
                  return;
                }
                await fetchAllMasterData();
                setShowHolidayModal(true);
              }}
            >
              Create Holiday Policy
            </DsButton>
            ) : null}
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredPolicies.length === 0 ? (
            <p className="ds-caption p-3 text-center text-[hsl(var(--ds-muted-fg))]">
              No holiday policies created yet
            </p>
          ) : (
            <table className="ds-table min-w-full text-sm">
              <thead className="ds-table-thead bg-[hsl(var(--ds-muted))]">
                <tr>
                  <th className="px-3 py-2 text-left">Policy</th>
                  <th className="px-3 py-2 text-left">Holidays</th>
                  <th className="px-3 py-2 text-left">RH / year</th>
                  <th className="px-3 py-2 text-left">Effective</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPolicies.map((policy) => (
                  <tr
                    key={policy.id}
                    className="border-t border-[hsl(var(--ds-border))]"
                  >
                    <td className="px-3 py-2 font-medium">{policy.name}</td>
                    <td className="px-3 py-2">{policy.holidays?.length || 0}</td>
                    <td className="px-3 py-2">
                      {policy.maxRestrictedHolidaysPerYear ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      {policy.effectiveDate ? formatDate(policy.effectiveDate) : "-"}
                    </td>
                    <td className="relative px-3 py-2 text-right">
                      <DsButton
                        type="button"
                        variant="outline"
                        size="sm"
                        className="!h-8 !w-8 !px-0"
                        onClick={() =>
                          setOpenActionFor((v) => (v === policy.id ? null : policy.id))
                        }
                      >
                        <DsIcon icon={MoreVertical} size="sm" />
                      </DsButton>
                      {openActionFor === policy.id ? (
                        <div className="absolute right-3 z-10 mt-1 w-28 overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
                          <button
                            type="button"
                            onClick={() => {
                              handleEditHolidayPolicy(policy);
                              setOpenActionFor(null);
                            }}
                            className="block w-full px-3 py-2 text-left text-xs hover:bg-[hsl(var(--ds-muted))]"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleDeleteHolidayPolicy(policy.id);
                              setOpenActionFor(null);
                            }}
                            className="block w-full px-3 py-2 text-left text-xs text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
                          >
                            Delete
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showHolidayBankPanel
        ? createPortal(
            <div
              className="fixed inset-0 z-[10900] flex items-center justify-center bg-black/50 p-4"
              role="presentation"
              onClick={() => setShowHolidayBankPanel(false)}
            >
              <div
                className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="holiday-bank-panel-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ds-page-panel-head flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <h3 id="holiday-bank-panel-title" className="ds-h4">
                      Holiday Bank
                    </h3>
                    <InfoHint title="Company catalog of holidays. Policies select from this bank. Grouped by calendar year." />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                      <DsIcon
                        icon={Search}
                        size="sm"
                        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
                      />
                      <DsInput
                        value={holidayBankSearch}
                        onChange={(e) => setHolidayBankSearch(e.target.value)}
                        placeholder="Search holiday..."
                        darkMode={darkMode}
                        className="w-56 rounded-xl pl-8"
                      />
                    </div>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowHolidayImportModal(true)}
                    >
                      Import
                    </DsButton>
                    <DsButton
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setShowHolidayBankModal(true);
                      }}
                    >
                      <DsIcon icon={Plus} size="sm" />
                      Add
                    </DsButton>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowHolidayBankPanel(false)}
                    >
                      Close
                    </DsButton>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 border-b border-[hsl(var(--ds-border))] px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setBankYearFilter("all")}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      bankYearFilter === "all"
                        ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                        : "bg-[hsl(var(--ds-muted))]"
                    }`}
                  >
                    All years
                  </button>
                  {bankYears.map((y) => (
                    <button
                      key={y}
                      type="button"
                      onClick={() => setBankYearFilter(String(y))}
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        String(bankYearFilter) === String(y)
                          ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                          : "bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      {y}
                    </button>
                  ))}
                </div>

                <div className="min-h-0 flex-1 overflow-auto p-3">
                  {bankGroupedEntries.length === 0 ? (
                    <p className="ds-caption p-6 text-center text-[hsl(var(--ds-muted-fg))]">
                      No holidays in bank yet
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {bankGroupedEntries.map(([year, list]) => (
                        <div key={String(year)}>
                          <div className="mb-2 flex items-center justify-between px-1">
                            <h4 className="text-sm font-semibold">{year}</h4>
                            <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                              {list.length} holiday{list.length === 1 ? "" : "s"}
                            </span>
                          </div>
                          <table className="ds-table mb-2 min-w-full text-sm">
                            <thead className="ds-table-thead bg-[hsl(var(--ds-muted))]">
                              <tr>
                                <th className="px-3 py-2 text-left">Holiday</th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {list.map((holiday) => (
                                <tr
                                  key={holiday.id}
                                  className="border-t border-[hsl(var(--ds-border))]"
                                >
                                  <td className="px-3 py-2 font-medium">
                                    {holiday.name}
                                  </td>
                                  <td className="px-3 py-2">
                                    {formatDate(holiday.date)}
                                  </td>
                                  <td className="px-3 py-2">
                                    <DsBadge tone={holidayTypeTone(holiday.type)}>
                                      {formatHolidayTypeLabel(holiday.type)}
                                    </DsBadge>
                                  </td>
                                  <td className="relative px-3 py-2 text-right">
                                    <div className="inline-flex gap-1">
                                      <DsButton
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="!h-8 !w-8 !px-0"
                                        title="Edit"
                                        onClick={() => {
                                          handleEditHolidayBank?.(holiday);
                                          setOpenBankActionFor(null);
                                        }}
                                      >
                                        <DsIcon icon={Pencil} size="sm" />
                                      </DsButton>
                                      <DsButton
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="!h-8 !w-8 !px-0 text-[hsl(var(--ds-danger))]"
                                        title="Delete"
                                        onClick={() => {
                                          handleRemoveFromHolidayBank(holiday.id);
                                          setOpenBankActionFor(null);
                                        }}
                                      >
                                        <DsIcon icon={Trash2} size="sm" />
                                      </DsButton>
                                      <DsButton
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="!h-8 !w-8 !px-0"
                                        onClick={() =>
                                          setOpenBankActionFor((v) =>
                                            v === holiday.id ? null : holiday.id
                                          )
                                        }
                                      >
                                        <DsIcon icon={MoreVertical} size="sm" />
                                      </DsButton>
                                    </div>
                                    {openBankActionFor === holiday.id ? (
                                      <div className="absolute right-3 z-20 mt-1 w-36 overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleEditHolidayBank?.(holiday);
                                            setOpenBankActionFor(null);
                                          }}
                                          className="block w-full px-3 py-2 text-left text-xs hover:bg-[hsl(var(--ds-muted))]"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleRemoveFromHolidayBank(holiday.id);
                                            setOpenBankActionFor(null);
                                          }}
                                          className="block w-full px-3 py-2 text-left text-xs text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default HolidayStructureTab;
